import React, { Reducer, useCallback, useMemo, useReducer, useState } from "react";
import { useTranslation } from "react-i18next"
import { glyphs } from "../../../Styles";
import { CalibrateEsfWfLocationModal, GateControlEsfWfLocationModal, StartEsfWfLocationModal, SystemControls } from "../..";
import { currentEsfViewTypes, EsfContext, EsfDispatcherT, EsfReducer, IEsfContext } from "../_EsfWalkThrough/EsfContext";
import { systemControleTypes } from "../../LocationFeeder/_LocationFeeder/lfPenContext";
import { AttentionTypes } from "../../../Definitions";
import { Graph_QL, System, useSystem } from "../../../Services";
import { useAppSelector } from "../../../Store";
import AutoTuneEsfWfLocationModal from "./autoTuneEsfLocationModal";

interface LfSystemControls_Props {
  system: System;
  focus_view?: boolean
}
/**
 * SystemControls
 * @returns A widget with btns to controle the system.
 */
export default function EsfSystemControls({ system, focus_view }: LfSystemControls_Props) {
  const {t} = useTranslation();
  const [isOn, setIsOn] = useState<boolean>(true)
  const [openModal, setOpenModal] = useState<systemControleTypes | null >(null)
  
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const { data: systemsState, emergencyStopSystem } = useSystem(connection); 

  const [_state, dispatch] = useReducer<Reducer<IEsfContext, EsfDispatcherT>>(EsfReducer, {
    farm: farm,
    domain: domain,
    connection: connection,
    system: system,
    selectedStations: undefined,
    locationResults: undefined,
    currentStationViewType: currentEsfViewTypes.systemControle,
    systemControlePen: {
      systemControleType: undefined
    },
    error: { Elementid: 0, errorText: "" }
  } as IEsfContext);

  const handleCloseModal = () => {
    if (!openModal) return;
    setOpenModal(null);
    dispatch({
      selectedStations: [],
      selectedSection: undefined,
      locationResults: undefined,
      systemControlePen: {
        systemControleType: undefined
      },
      error: { Elementid: 0, errorText: "" }
    });
  }

  const { attentionType } = useMemo(() => {
    let _attentionType = systemsState[domain]?.data.find((i) => i.system_id === system.system_id)?.system_attention_type ?? AttentionTypes.good;

    return {attentionType: _attentionType}
  }, [domain, system, systemsState])

  const handleButtonClick = useCallback((i: number) => {
    if (i === 0) { setOpenModal(systemControleTypes.startSection); dispatch({ systemControlePen: { systemControleType: systemControleTypes.startSection } }) }
    if (i === 1) { setOpenModal(systemControleTypes.gateControle); dispatch({ systemControlePen: { systemControleType: systemControleTypes.gateControle }})}
    if (i === 2) { setOpenModal(systemControleTypes.calibrateSection); dispatch({ systemControlePen: { systemControleType: systemControleTypes.calibrateSection }})}
    if (i === 3) { setOpenModal(systemControleTypes.autoTune); dispatch({ systemControlePen: { systemControleType: systemControleTypes.autoTune }})}
    if (i === 4) {
    if (isOn) {
      if (window.confirm(`${t("systemControls.general.confirmStop", {defaultValue: "Would you like to proceed the emergency stop of the system"})}?`)) { setIsOn(false); emergencyStopSystem(system.system_id) }
    } else {
      setIsOn(true);
    }
  }
  }, [isOn, t, emergencyStopSystem]);
  
  return (
    <EsfContext.Provider value={{ state: _state, dispatch }}>
      <SystemControls
        handleButtonClick={handleButtonClick}
        attentionType={attentionType}
        systemRunning={isOn}
        focus_view={focus_view}
        buttonContents={[
          t("systemControls.general.startLocation", { defaultValue: "start location" }),
          t("systemControls.general.gateControl", { defaultValue: "gate control" }),
          t("systemControls.general.calibrateLocation", {defaultValue: "calibrate location"}),
          t("systemControls.general.autotuneAntenna", { defaultValue: "autotune antenna" })
        ]}
        buttonEnhancers={[
          glyphs.Start({ size: focus_view ? "40px" : "35px" }),
          glyphs.Gate({ size: focus_view ? "40px" : "35px" }),
          glyphs.Calibrate({ size: focus_view ? "40px" : "35px" }),
          glyphs.Calibrate({ size: focus_view ? "40px" : "35px" })
        ]}
      />
      
      {openModal === systemControleTypes.startSection && <StartEsfWfLocationModal onClose={handleCloseModal} focus_view={focus_view} />}
      {openModal === systemControleTypes.calibrateSection && <CalibrateEsfWfLocationModal onClose={handleCloseModal} focus_view={focus_view} />}
      {openModal === systemControleTypes.autoTune && <AutoTuneEsfWfLocationModal onClose={handleCloseModal} focus_view={focus_view} />}
      {openModal === systemControleTypes.gateControle && <GateControlEsfWfLocationModal onClose={handleCloseModal} focus_view={focus_view} />}
    </EsfContext.Provider>
  )
}

